import { API_BASE_URL } from "../config";
import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import KYCModal from "./KYCModal";
import StatementExport from "./StatementExport";

const Funds = () => {
  const { user, wallet, refreshWallet, fetchProfile } = useContext(AuthContext);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modals state
  const [isDepositOpen, setIsDepositOpen] = useState(false);
  const [isWithdrawOpen, setIsWithdrawOpen] = useState(false);
  const [isKycOpen, setIsKycOpen] = useState(false);
  const [isStatementOpen, setIsStatementOpen] = useState(false);

  // Form state
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const fetchWalletData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/client/wallet`);
      setTransactions(res.data.transactions || []);
    } catch (err) {
      console.error("Error fetching transactions", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWalletData();
  }, []);

  const handleDepositSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");
    setActionLoading(true);

    try {
      const res = await axios.post(`${API_BASE_URL}/api/client/deposit`, {
        amount: Number(amount),
        notes,
      });

      setMessage(res.data.message);
      setAmount("");
      setNotes("");
      await refreshWallet();
      await fetchWalletData();
      setTimeout(() => setIsDepositOpen(false), 1500);
    } catch (err) {
      setError(err.response?.data?.message || "Deposit failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleWithdrawSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");
    setActionLoading(true);

    try {
      const res = await axios.post(`${API_BASE_URL}/api/client/withdraw`, {
        amount: Number(amount),
        notes,
      });

      setMessage(res.data.message);
      setAmount("");
      setNotes("");
      await refreshWallet();
      await fetchWalletData();
      setTimeout(() => setIsWithdrawOpen(false), 1500);
    } catch (err) {
      setError(err.response?.data?.message || "Withdrawal failed");
    } finally {
      setActionLoading(false);
    }
  };

  const getKycBanner = () => {
    if (!user) return null;
    if (user.kycStatus === "verified") {
      return (
        <div style={{ backgroundColor: "#e8f5e9", color: "#2e7d32", padding: "10px 16px", borderRadius: "6px", marginBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>✅ Your KYC status is <strong>Verified</strong>. All deposit and withdrawal features are unlocked.</span>
        </div>
      );
    } else if (user.kycStatus === "pending") {
      return (
        <div style={{ backgroundColor: "#fff8e1", color: "#f57f17", padding: "10px 16px", borderRadius: "6px", marginBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>⏳ Your KYC details are currently <strong>Pending Verification</strong> by Admin.</span>
        </div>
      );
    } else if (user.kycStatus === "rejected") {
      return (
        <div style={{ backgroundColor: "#ffebee", color: "#c62828", padding: "10px 16px", borderRadius: "6px", marginBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>❌ Your KYC was <strong>Rejected</strong>: {user.kycDetails?.rejectedReason || "Documentation failed."}</span>
          <button onClick={() => setIsKycOpen(true)} style={{ backgroundColor: "#c62828", color: "#fff", border: "none", padding: "6px 12px", borderRadius: "4px", cursor: "pointer" }}>Re-submit KYC</button>
        </div>
      );
    } else {
      return (
        <div style={{ backgroundColor: "#eef6ff", color: "#1976d2", padding: "10px 16px", borderRadius: "6px", marginBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>ℹ️ Complete your KYC verification to enable virtual fund withdrawals.</span>
          <button onClick={() => setIsKycOpen(true)} style={{ backgroundColor: "#1976d2", color: "#fff", border: "none", padding: "6px 12px", borderRadius: "4px", cursor: "pointer", fontWeight: "bold" }}>Complete KYC Now</button>
        </div>
      );
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      {getKycBanner()}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <div>
          <h2 style={{ margin: 0, color: "#333" }}>Virtual Wallet & Paper Trading Capital</h2>
          <p style={{ margin: "4px 0 0", color: "#666", fontSize: "14px" }}>Simulated cash and margin exposure tracking</p>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <button onClick={() => { setIsDepositOpen(true); setMessage(""); setError(""); }} style={{ backgroundColor: "#4caf50", color: "#fff", border: "none", padding: "10px 18px", borderRadius: "6px", cursor: "pointer", fontWeight: "bold" }}>
            + Add Funds (Simulated)
          </button>
          <button onClick={() => { setIsWithdrawOpen(true); setMessage(""); setError(""); }} style={{ backgroundColor: "#387ed1", color: "#fff", border: "none", padding: "10px 18px", borderRadius: "6px", cursor: "pointer", fontWeight: "bold" }}>
            ⤓ Withdraw Funds
          </button>
          <button onClick={() => setIsStatementOpen(true)} style={{ backgroundColor: "#f5f5f5", color: "#333", border: "1px solid #ccc", padding: "10px 14px", borderRadius: "6px", cursor: "pointer" }}>
            📄 Download Statement
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", marginBottom: "24px" }}>
        <div style={{ backgroundColor: "#ffffff", padding: "18px", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", borderLeft: "4px solid #4caf50" }}>
          <div style={{ fontSize: "12px", color: "#666", textTransform: "uppercase" }}>Available Margin</div>
          <div style={{ fontSize: "24px", fontWeight: "bold", color: "#2e7d32", marginTop: "6px" }}>
            ₹{wallet?.marginAvailable?.toLocaleString('en-IN') || '0.00'}
          </div>
        </div>

        <div style={{ backgroundColor: "#ffffff", padding: "18px", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", borderLeft: "4px solid #ff9800" }}>
          <div style={{ fontSize: "12px", color: "#666", textTransform: "uppercase" }}>Used Margin</div>
          <div style={{ fontSize: "24px", fontWeight: "bold", color: "#e65100", marginTop: "6px" }}>
            ₹{wallet?.marginUsed?.toLocaleString('en-IN') || '0.00'}
          </div>
        </div>

        <div style={{ backgroundColor: "#ffffff", padding: "18px", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", borderLeft: "4px solid #387ed1" }}>
          <div style={{ fontSize: "12px", color: "#666", textTransform: "uppercase" }}>Total Virtual Balance</div>
          <div style={{ fontSize: "24px", fontWeight: "bold", color: "#1565c0", marginTop: "6px" }}>
            ₹{wallet?.virtualBalance?.toLocaleString('en-IN') || '0.00'}
          </div>
        </div>

        <div style={{ backgroundColor: "#ffffff", padding: "18px", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", borderLeft: "4px solid #9c27b0" }}>
          <div style={{ fontSize: "12px", color: "#666", textTransform: "uppercase" }}>Total Deposits / Topups</div>
          <div style={{ fontSize: "24px", fontWeight: "bold", color: "#6a1b9a", marginTop: "6px" }}>
            ₹{wallet?.totalDeposits?.toLocaleString('en-IN') || '0.00'}
          </div>
        </div>
      </div>

      {/* Transaction History */}
      <div style={{ backgroundColor: "#ffffff", borderRadius: "8px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <h3 style={{ margin: "0 0 16px 0", color: "#333" }}>Transaction & Ledger History</h3>

        {loading ? (
          <p>Loading transactions...</p>
        ) : transactions.length === 0 ? (
          <p style={{ color: "#888" }}>No wallet transactions yet.</p>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
            <thead>
              <tr style={{ backgroundColor: "#f8f9fa", textAlign: "left", color: "#666", borderBottom: "2px solid #eee" }}>
                <th style={{ padding: "10px" }}>Date & Time</th>
                <th style={{ padding: "10px" }}>Type</th>
                <th style={{ padding: "10px" }}>Amount</th>
                <th style={{ padding: "10px" }}>Status</th>
                <th style={{ padding: "10px" }}>Details / Notes</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx) => (
                <tr key={tx._id} style={{ borderBottom: "1px solid #eee" }}>
                  <td style={{ padding: "10px", color: "#555" }}>
                    {new Date(tx.requestedAt).toLocaleString('en-IN')}
                  </td>
                  <td style={{ padding: "10px", fontWeight: 500 }}>
                    {tx.type.replace(/_/g, " ").toUpperCase()}
                  </td>
                  <td style={{ padding: "10px", fontWeight: "bold", color: tx.type.includes("credit") || tx.type.includes("deposit") ? "#2e7d32" : "#c62828" }}>
                    {tx.type.includes("debit") || tx.type.includes("withdrawal") ? "-" : "+"} ₹{tx.amount?.toLocaleString('en-IN')}
                  </td>
                  <td style={{ padding: "10px" }}>
                    <span
                      style={{
                        padding: "3px 8px",
                        borderRadius: "12px",
                        fontSize: "12px",
                        fontWeight: "bold",
                        backgroundColor:
                          tx.status === "completed" || tx.status === "approved"
                            ? "#e8f5e9"
                            : tx.status === "pending"
                            ? "#fff8e1"
                            : "#ffebee",
                        color:
                          tx.status === "completed" || tx.status === "approved"
                            ? "#2e7d32"
                            : tx.status === "pending"
                            ? "#f57f17"
                            : "#c62828",
                      }}
                    >
                      {tx.status?.toUpperCase()}
                    </span>
                  </td>
                  <td style={{ padding: "10px", color: "#666" }}>
                    {tx.notes} {tx.rejectionReason && <span style={{ color: "#c62828" }}>(Reason: {tx.rejectionReason})</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Deposit Modal */}
      {isDepositOpen && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h3 style={{ marginTop: 0, color: "#4caf50" }}>Simulated Fund Top-Up</h3>
            <p style={{ fontSize: "13px", color: "#666" }}>Add virtual funds to your paper trading wallet (demo sandbox only).</p>

            {message && <div style={{ backgroundColor: "#e8f5e9", color: "#2e7d32", padding: "10px", borderRadius: "4px", marginBottom: "12px" }}>{message}</div>}
            {error && <div style={{ backgroundColor: "#ffebee", color: "#c62828", padding: "10px", borderRadius: "4px", marginBottom: "12px" }}>{error}</div>}

            <form onSubmit={handleDepositSubmit}>
              <div style={{ marginBottom: "14px" }}>
                <label style={labelStyle}>Amount (₹)</label>
                <input
                  type="number"
                  min="100"
                  step="100"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="e.g. 50000"
                  required
                  style={inputStyle}
                />
              </div>
              <div style={{ marginBottom: "14px" }}>
                <label style={labelStyle}>Notes / Reference</label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Demo deposit top-up"
                  style={inputStyle}
                />
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
                <button type="button" onClick={() => setIsDepositOpen(false)} style={btnGreyStyle}>Cancel</button>
                <button type="submit" disabled={actionLoading} style={{ ...btnGreenStyle }}>
                  {actionLoading ? "Processing..." : "Add Virtual Capital"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Withdrawal Modal */}
      {isWithdrawOpen && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h3 style={{ marginTop: 0, color: "#387ed1" }}>Simulated Fund Withdrawal</h3>
            <p style={{ fontSize: "13px", color: "#666" }}>Withdraw virtual funds from your paper trading balance.</p>

            {message && <div style={{ backgroundColor: "#e8f5e9", color: "#2e7d32", padding: "10px", borderRadius: "4px", marginBottom: "12px" }}>{message}</div>}
            {error && <div style={{ backgroundColor: "#ffebee", color: "#c62828", padding: "10px", borderRadius: "4px", marginBottom: "12px" }}>{error}</div>}

            <form onSubmit={handleWithdrawSubmit}>
              <div style={{ marginBottom: "14px" }}>
                <label style={labelStyle}>Withdrawal Amount (₹)</label>
                <input
                  type="number"
                  min="100"
                  max={wallet?.marginAvailable || 0}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={`Max available: ₹${wallet?.marginAvailable || 0}`}
                  required
                  style={inputStyle}
                />
              </div>
              <div style={{ marginBottom: "14px" }}>
                <label style={labelStyle}>Notes / Bank Account Details</label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Simulated HDFC Bank payout"
                  style={inputStyle}
                />
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
                <button type="button" onClick={() => setIsWithdrawOpen(false)} style={btnGreyStyle}>Cancel</button>
                <button type="submit" disabled={actionLoading} style={{ ...btnBlueStyle }}>
                  {actionLoading ? "Submitting..." : "Request Withdrawal"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* KYC Modal */}
      {isKycOpen && <KYCModal onClose={() => setIsKycOpen(false)} onSuccess={() => { fetchProfile(); setIsKycOpen(false); }} />}

      {/* Statement Modal */}
      {isStatementOpen && <StatementExport onClose={() => setIsStatementOpen(false)} />}
    </div>
  );
};

const modalOverlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0,0,0,0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 2000,
};

const modalContentStyle = {
  backgroundColor: "#fff",
  padding: "24px",
  borderRadius: "10px",
  width: "100%",
  maxWidth: "440px",
  boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
};

const labelStyle = {
  display: "block",
  fontSize: "13px",
  fontWeight: 500,
  marginBottom: "6px",
  color: "#444",
};

const inputStyle = {
  width: "100%",
  padding: "10px",
  borderRadius: "6px",
  border: "1px solid #ccc",
  fontSize: "14px",
  boxSizing: "border-box",
};

const btnGreyStyle = {
  padding: "10px 16px",
  backgroundColor: "#eee",
  color: "#333",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
};

const btnGreenStyle = {
  padding: "10px 18px",
  backgroundColor: "#4caf50",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  fontWeight: "bold",
  cursor: "pointer",
};

const btnBlueStyle = {
  padding: "10px 18px",
  backgroundColor: "#387ed1",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  fontWeight: "bold",
  cursor: "pointer",
};

export default Funds;
